import PolicyLayout, { PolicySection, P, UL, LI, Callout } from "./PolicyLayout";

const TOC = [
  { id: "overview", label: "Overview" },
  { id: "what-we-collect", label: "What we collect" },
  { id: "how-we-use", label: "How we use it" },
  { id: "sharing", label: "Who we share with" },
  { id: "cookies", label: "Cookies & tracking" },
  { id: "retention", label: "Retention" },
  { id: "your-rights", label: "Your choices" },
  { id: "ca-residents", label: "California residents" },
  { id: "children", label: "Children" },
  { id: "security", label: "Security" },
  { id: "changes", label: "Changes" },
  { id: "contact", label: "Contact" },
];

export default function PrivacyPolicy() {
  return (
    <PolicyLayout
      eyebrow="Privacy"
      title="Privacy Policy"
      updated="February 2026"
      intro="Coachly collects the least amount of personal data we need to run a safe, useful coaching marketplace. This page explains what we collect, why, and the choices you have."
      toc={TOC}
      testId="policy-privacy"
    >
      <PolicySection id="overview" title="1. Overview">
        <P>
          This Privacy Policy covers Coachly, Inc. (“Coachly,” “we,” “us,” “our”)
          and the coachly.app website and services. Personal information means
          information that can identify you.
        </P>
        <Callout>
          Coachly is a U.S. company. If you're using the Service from outside the
          U.S., you're transferring your data into the U.S. and consenting to that
          transfer.
        </Callout>
      </PolicySection>

      <PolicySection id="what-we-collect" title="2. What we collect">
        <P>We collect the following categories of information:</P>
        <UL>
          <LI><strong>Account &amp; contact:</strong> name, email, city, phone (optional), password hash if you sign up with email.</LI>
          <LI><strong>Coach profile:</strong> bio, headshot, specialties, price, availability, timezone, credentials or certifications you choose to share.</LI>
          <LI><strong>Bookings:</strong> the coach you booked, session date/time, session mode (online / in-person), duration, and price.</LI>
          <LI><strong>Payments:</strong> we don't store your card details. Card data is entered on Stripe's PCI-compliant checkout. Stripe returns a payment token, an amount, a currency, and (for coaches) a Stripe Connect account ID.</LI>
          <LI><strong>Messages &amp; reviews:</strong> chat between clients and coaches, and reviews you leave.</LI>
          <LI><strong>Device &amp; usage:</strong> IP address, browser type, referring page, actions you take on the site (via cookies and standard server logs).</LI>
        </UL>
      </PolicySection>

      <PolicySection id="how-we-use" title="3. How we use it">
        <UL>
          <LI>To run the platform — show coach profiles, take bookings, send transactional emails, process payouts.</LI>
          <LI>To keep the platform safe — detect fraud, respond to reports, enforce our{" "}
            <a href="/trust-safety" className="text-[#2563EB] hover:underline">Trust &amp; Safety Policy</a>.
          </LI>
          <LI>To improve the product — usage analytics on aggregated, non-identifying data.</LI>
          <LI>To meet legal obligations — tax reporting, subpoenas, etc.</LI>
        </UL>
        <P>
          We <strong>do not sell your personal information</strong>. We do not use your
          Coachly messages or session content to train external AI models.
        </P>
      </PolicySection>

      <PolicySection id="sharing" title="4. Who we share with">
        <P>Only these categories of recipients:</P>
        <UL>
          <LI><strong>Coaches and clients you interact with:</strong> your name, avatar, and the messages you send them.</LI>
          <LI><strong>Service providers:</strong> Stripe (payments &amp; payouts), Resend / our email provider (transactional emails), MongoDB Atlas (database hosting), and Kubernetes hosting infrastructure. Each provider is under a written contract to protect your data.</LI>
          <LI><strong>Law enforcement or courts:</strong> only when we're legally required to, or when we believe in good faith it's necessary to prevent harm.</LI>
          <LI><strong>A successor:</strong> if Coachly is acquired or merged, information may transfer to the successor company under the same protections.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="cookies" title="5. Cookies &amp; tracking">
        <P>
          We use a small number of cookies for essential functions (keeping you logged
          in, remembering your preferences) and lightweight analytics that measure
          aggregate traffic. We don't use third-party ad-tech cookies. You can clear or
          block cookies in your browser at any time — some features may not work as well
          if you do.
        </P>
      </PolicySection>

      <PolicySection id="retention" title="6. How long we keep it">
        <UL>
          <LI>Account &amp; profile data: for as long as your account is open, plus 90 days after deletion for backups and dispute handling.</LI>
          <LI>Booking, payment, and tax records: at least 7 years to comply with U.S. tax and financial-reporting rules.</LI>
          <LI>Messages: for as long as your account is open, then anonymized.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="your-rights" title="7. Your choices">
        <UL>
          <LI><strong>Access &amp; correction:</strong> you can view and edit most of your data from your dashboard.</LI>
          <LI><strong>Deletion:</strong> email <a href="mailto:privacy@coachly.app" className="text-[#2563EB] hover:underline">privacy@coachly.app</a> and we'll delete your account within 30 days, except records we're required to keep by law.</LI>
          <LI><strong>Marketing opt-out:</strong> transactional emails (bookings, receipts, reminders) are required to run the Service and cannot be turned off while your account is active. All promotional emails include an unsubscribe link.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="ca-residents" title="8. California residents">
        <P>
          If you live in California, you have additional rights under the CCPA / CPRA,
          including the right to know, delete, correct, and limit the use of sensitive
          personal information. To exercise these rights, email{" "}
          <a href="mailto:privacy@coachly.app" className="text-[#2563EB] hover:underline">privacy@coachly.app</a>{" "}
          with the subject line “California privacy request.” We do not knowingly “sell”
          or “share” personal information in the CCPA sense.
        </P>
      </PolicySection>

      <PolicySection id="children" title="9. Children">
        <P>
          Coachly is not directed at anyone under 18. We do not knowingly collect
          personal information from minors. If you believe a minor has created an
          account, please write to us and we'll remove it.
        </P>
      </PolicySection>

      <PolicySection id="security" title="10. Security">
        <P>
          We encrypt data in transit (HTTPS) and at rest. Card data never touches
          Coachly's servers — it's tokenized by Stripe. No system is perfectly secure,
          so we can't guarantee security absolutely, but we work hard to protect your
          information.
        </P>
      </PolicySection>

      <PolicySection id="changes" title="11. Changes to this policy">
        <P>
          If we make a material change we'll email you and post a notice on the site at
          least 14 days before it takes effect.
        </P>
      </PolicySection>

      <PolicySection id="contact" title="12. Contact">
        <P>
          Privacy questions? Email{" "}
          <a href="mailto:privacy@coachly.app" className="text-[#2563EB] hover:underline">privacy@coachly.app</a>.
        </P>
      </PolicySection>
    </PolicyLayout>
  );
}
